<template>
  <div class="quicksearch-response-item-address">
    <div
      v-if="shippingPoint.location?.locationName"
      class="quicksearch-response-item-address__name font-bold"
    >
      <QuickQuickSearchResponseItemHighlightField
        :value="shippingPoint.location.locationName"
        field-name="location.locationName"
        :highlight="effectiveHighlights"
      />
    </div>
    <div
      v-if="shippingPoint.location?.code"
      class="quicksearch-response-item-address__code"
    >
      <QuickQuickSearchResponseItemHighlightField
        :value="shippingPoint.location.code"
        field-name="location.code"
        :highlight="effectiveHighlights"
      />
    </div>
    <div
      v-if="shippingPoint.location?.addressLine1"
      class="quicksearch-response-item-address__address-line"
    >
      <QuickQuickSearchResponseItemHighlightField
        :value="shippingPoint.location.addressLine1"
        field-name="location.addressLine1"
        :highlight="effectiveHighlights"
      />
    </div>
    <div
      v-if="shippingPoint.location?.addressLine2"
      class="quicksearch-response-item-address__address-line"
    >
      <QuickQuickSearchResponseItemHighlightField
        :value="shippingPoint.location.addressLine2"
        field-name="location.addressLine2"
        :highlight="effectiveHighlights"
      />
    </div>
    <div
      v-if="shippingPoint.location?.addressLine3"
      class="quicksearch-response-item-address__address-line"
    >
      <QuickQuickSearchResponseItemHighlightField
        :value="shippingPoint.location.addressLine3"
        field-name="location.addressLine3"
        :highlight="effectiveHighlights"
      />
    </div>
    <div class="quicksearch-response-item-address__citystatepostal">
      <span
        v-if="shippingPoint.location?.city"
        class="quicksearch-response-item-address__citystatepostal__city"
      >
        <QuickQuickSearchResponseItemHighlightField
          :value="shippingPoint.location.city"
          field-name="location.city"
          :highlight="effectiveHighlights"
        />
      </span>
      <span
        v-if="shippingPoint.location?.state"
        class="quicksearch-response-item-address__citystatepostal__state"
      >
        <span>{{ shippingPoint.location.state }}</span>
      </span>
      <span>
        <QuickQuickSearchResponseItemHighlightField
          v-if="shippingPoint.location?.postalCode"
          :value="shippingPoint.location.postalCode"
          field-name="location.postalCode"
          :highlight="effectiveHighlights"
        />
      </span>
    </div>
    <div
      v-if="
        shippingPoint.location?.country &&
        shippingPoint.location.country != 'USA'
      "
      class="quicksearch-response-item-address__country"
    >
      {{ shippingPoint.location.country }}
    </div>
    <div
      v-if="mostImportantDate?.dateTime"
      class="quicksearch-response-item-address__date"
    >
      {{ renderDateTime(mostImportantDate.dateTime, { timeFormat: 'NONE' }) }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';

import { renderDateTime } from '@rygen/rygen-ui/i18n';
import type { OrderShippingPoint as OrderShippingPointQuickSearchResult } from '@rygen/tms-search-model/search/orderShippingPoint';
import type { ShipmentStop as ShipmentStopQuickSearchResult } from '@rygen/tms-search-model/search/shipmentStop';

import { getQuickSearchShippingPointMostImportantDate } from '@/model/QuickQuickSearchResponse';

import QuickQuickSearchResponseItemHighlightField from '@/components/search/QuickQuickSearchResponseItemHighlightField.vue';

const props = defineProps<{
  shippingPoint:
    OrderShippingPointQuickSearchResult | ShipmentStopQuickSearchResult;
  shippingPointType: 'origin' | 'destination';
  highlight?: Record<string, string[]>;
}>();

const { shippingPoint, shippingPointType, highlight } = toRefs(props);

const mostImportantDate = computed(() => {
  return getQuickSearchShippingPointMostImportantDate(shippingPoint.value);
});

const effectiveHighlights = computed(() => {
  const highlights = highlight.value || {};

  return Object.keys(highlights)
    .filter((k) => k.startsWith(shippingPointType.value))
    .reduce(
      (agg, k) => {
        const shippingPointTypePrefixPattern = RegExp(
          '^' + shippingPointType.value + '\\.',
          'g',
        );
        const newKey = k.replaceAll(shippingPointTypePrefixPattern, '');
        agg[newKey] = highlights[k];
        return agg;
      },
      {} as Record<string, string[]>,
    );
});
</script>

<style>
.quicksearch-response-item-address {
  .quicksearch-response-item-address__address-line,
  .quicksearch-response-item-address__zone-information,
  .quicksearch-response-item-address__reference-number,
  .quicksearch-response-item-address__country,
  .quicksearch-response-item-address__code {
    color: var(--p-surface-600);
  }

  .quicksearch-response-item-address__address-line,
  .quicksearch-response-item-address__city-state-postal,
  .quicksearch-response-item-address__country,
  .quicksearch-response-item-address__date {
    font-size: var(--p-text-sm);
  }

  .quicksearch-response-item-address__citystatepostal {
    .quicksearch-response-item-address__citystatepostal__city:after {
      content: ', ';
      white-space: pre;
    }

    .quicksearch-response-item-address__citystatepostal__state:after {
      content: ' ';
      white-space: pre;
    }
  }

  .quicksearch-response-item-address__date {
    padding: 2px 0 0 0;
    color: var(--p-surface-400);
    font-size: small;
  }
}
</style>
